import { parseNodeToSympy } from "./buildSympyCode";
import { SympyBuildError } from "./sympy-builder";

import type { FunctionDef } from "./buildSympyCode";
import type { NodeJson } from "@corca/data";

const toInlineNode = (nodes: NodeJson[]): NodeJson => ({
  type: "inline",
  props: {},
  children: nodes,
});

/** An earlier `variable = expression` line the hint pipeline substitutes into the expression. */
export type PriorAssignment = {
  variableName: string; // e.g., "a", "x_1"
  rhsNodes: NodeJson[]; // NodeJson[] of everything after "="
  wrapperId: string;
};

/**
 * Build substitutions map from prior assignments.
 * Returns a map of variable name to SymPy expression string.
 * Applies transitive substitution: if a=5 and b=a+10, then b substitutes to 5+10.
 *
 * An assignment whose right-hand side does not translate is skipped rather than fatal — the line
 * being hinted does not depend on every earlier line parsing.
 */
export function buildSubstitutions(
  priorAssignments: PriorAssignment[],
  mainVariables: Map<string, string>,
  emitSiUnitsOverride?: boolean | null,
): {
  substitutions: Map<string, string>;
  usedWrapperIds: Set<string>;
  hasDecimal: boolean;
  warnings: string[];
} {
  const substitutions = new Map<string, string>();
  const usedWrapperIds = new Set<string>();
  const warnings: string[] = [];
  let hasDecimal = false;

  const assignmentsByName = new Map<string, PriorAssignment>();
  for (const assignment of priorAssignments) {
    assignmentsByName.set(assignment.variableName, assignment);
  }

  const resolveSubstitution = (varName: string, visited = new Set<string>()): string | null => {
    if (visited.has(varName)) return null;
    if (substitutions.has(varName)) return substitutions.get(varName)!;

    const assignment = assignmentsByName.get(varName);
    if (!assignment) return null;

    visited.add(varName);
    usedWrapperIds.add(assignment.wrapperId);

    const rhsInline: NodeJson = {
      type: "inline",
      props: {},
      children: assignment.rhsNodes,
    };

    // Shared namespace ensures the same math variable always maps to the same varN symbol
    let rhsParsed;
    try {
      rhsParsed = parseNodeToSympy(rhsInline, mainVariables, emitSiUnitsOverride);
    } catch (error) {
      // Only a rejected right-hand side is skippable; a bug is the host's to report.
      if (!(error instanceof SympyBuildError)) throw error;
      warnings.push(`Skipped the assignment to ${varName}: ${error.message}`);
      return null;
    }
    warnings.push(...rhsParsed.warnings);
    if (!rhsParsed.expr) return null;
    if (rhsParsed.hasDecimal) hasDecimal = true;

    let resolvedExpr = rhsParsed.expr;
    for (const [rhsVarName] of rhsParsed.variables.entries()) {
      const innerExpr = resolveSubstitution(rhsVarName, visited);

      if (innerExpr) {
        const varSymbol = mainVariables.get(rhsVarName)!;
        resolvedExpr = resolvedExpr.replace(
          new RegExp(`\\b${varSymbol}\\b`, "g"),
          `(${innerExpr})`,
        );
      }
    }

    substitutions.set(varName, resolvedExpr);
    return resolvedExpr;
  };

  for (const varName of mainVariables.keys()) {
    resolveSubstitution(varName);
  }

  return { substitutions, usedWrapperIds, hasDecimal, warnings };
}

/** An earlier `f(x) = body` line, which becomes a SymPy Lambda rather than a substitution. */
export type PriorFunctionDef = {
  funcName: string; // e.g., "f"
  paramNames: string[]; // e.g., ["x"] or ["x", "y"]
  rhsNodes: NodeJson[]; // NodeJson[] of the function body after "="
  wrapperId: string;
};

/**
 * Build SymPy Lambda definitions from prior function definitions.
 * Last-write-wins: if the same function name is defined multiple times, the latest definition is used.
 *
 * A definition whose body does not translate is warned about and skipped, as in `buildSubstitutions`.
 */
export function buildFunctionDefSubstitutions(
  priorFunctionDefs: PriorFunctionDef[],
  mainVariables: Map<string, string>,
  emitSiUnitsOverride?: boolean | null,
): {
  functionDefs: Map<string, FunctionDef & { wrapperId: string }>;
  warnings: string[];
} {
  const functionDefs = new Map<string, FunctionDef & { wrapperId: string }>();
  const warnings: string[] = [];

  for (const funcDef of priorFunctionDefs) {
    const bodyVariables = new Map<string, string>(mainVariables);

    for (const paramName of funcDef.paramNames) {
      if (!bodyVariables.has(paramName)) {
        bodyVariables.set(paramName, `var${bodyVariables.size}`);
      }
    }

    let rhsParsed;
    try {
      rhsParsed = parseNodeToSympy(
        toInlineNode(funcDef.rhsNodes),
        bodyVariables,
        emitSiUnitsOverride,
      );
    } catch (error) {
      if (!(error instanceof SympyBuildError)) throw error;
      warnings.push(`Failed to parse function body for ${funcDef.funcName}: ${error.message}`);
      continue;
    }

    warnings.push(...rhsParsed.warnings);

    if (!rhsParsed.expr) {
      warnings.push(`Failed to parse function body for ${funcDef.funcName}: empty expression`);
      continue;
    }

    // Propagate any new variables discovered in the body (excluding params)
    const paramNameSet = new Set(funcDef.paramNames);
    for (const [name, symbol] of bodyVariables.entries()) {
      if (!mainVariables.has(name) && !paramNameSet.has(name)) {
        mainVariables.set(name, symbol);
      }
    }

    const paramSymbols = funcDef.paramNames.map((p) => bodyVariables.get(p)!);

    // Last-write-wins: if two defs share a funcName, the later one overwrites
    functionDefs.set(funcDef.funcName, {
      paramSymbols,
      paramNames: funcDef.paramNames,
      bodyExpr: rhsParsed.expr,
      wrapperId: funcDef.wrapperId,
    });
  }

  return { functionDefs, warnings };
}
